/**
 * Dynamic diagnosis dimension card for template-defined dimensions.
 *
 * Renders facts, interpretation, and a typed value (boolean, number, string, or string[])
 * based on what the analysis produced for each custom dimension.
 */

import { cn } from '@/lib/utils';
import type { DynamicDiagnosisDimension } from '@/types';

export interface DynamicDiagnosisDimensionCardProps {
  title: string;
  dimension: DynamicDiagnosisDimension;
  icon?: React.ReactNode;
  className?: string;
}

/**
 * Format a dimension value for display
 */
function formatValue(value: boolean | number | string | string[]): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * Get badge color for a value (heuristic based on type)
 */
function getValueBadgeColor(value: boolean | number | string | string[]): string {
  if (typeof value === 'boolean') {
    return value
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-red-100 text-red-700 border-red-200';
  }
  return 'bg-blue-100 text-blue-700 border-blue-200';
}

export function DynamicDiagnosisDimensionCard({
  title,
  dimension,
  icon,
  className,
}: DynamicDiagnosisDimensionCardProps) {
  return (
    <div className={cn('', className)}>
      {/* Section Header with value badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon && <span className="text-gray-400">{icon}</span>}
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        </div>
        {dimension.value !== undefined && dimension.value !== null && (
          <span
            className={cn(
              'px-2 py-0.5 text-xs font-medium rounded border',
              getValueBadgeColor(dimension.value)
            )}
          >
            {formatValue(dimension.value)}
          </span>
        )}
      </div>

      {/* Facts List */}
      {dimension.facts.length > 0 && (
        <div className="mb-3">
          <span className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2 block">
            Facts
          </span>
          <ul className="space-y-1.5">
            {dimension.facts.map((fact, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                <span className="text-blue-400 mt-0.5 flex-shrink-0">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 8 8">
                    <circle cx="4" cy="4" r="4" />
                  </svg>
                </span>
                <span className="leading-relaxed">{fact}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Interpretation */}
      <div>
        <span className="text-xs font-medium uppercase tracking-wider text-gray-400 mb-2 block">
          Interpretation
        </span>
        <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
          {dimension.interpretation}
        </p>
      </div>
    </div>
  );
}
